import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Cargo } from '../interfaces/cargo';
import { AssignVehicle } from '../interfaces/assignVehicle';
import { User } from '../interfaces/user';
import { Vehicle } from '../interfaces/vehicle';
import { PermissionRole } from '../resources/permission-role';
import { Permission } from '../resources/permission';
import { Companies } from '../resources/companies';
import { Utils } from '../resources/utils';
import { AuthService } from '../services/authentication.service';
import { CargoPaymentsService } from '../services/cargo-payments.service';
import { CompanyService } from '../services/company.service';
import { CargoService } from '../services/cargo.service';
import { AdminUsersService } from 'src/app/modules/administration/admin-users/admin-users.service';
import { MandatoryDocument, VehiclesService } from 'src/app/modules/administration/vehicles/list-vehicles.service';
import { AccountService } from 'src/app/modules/account/account.service';
import { ValidationRule } from '../interfaces/validation-rule';
import { ServiceMessages } from '../messages/service-messages.enum';
import { VehicleMessages } from '../messages/vehicle-messages.enum';
import { DriverLicenseCategory } from '../interfaces/driverLicenseCategory';
import { licenseCategories } from '../resources/licenseCategories';
import { DateManager } from './date.manager';
import { Patterns } from '../resources/patterns';
import { ConfigurationSaaSId } from '../interfaces/saas-config';
import { CatalogItem } from '../interfaces/catalogItem';
import { VEHICLE_TRIP_VALIDATION_EXCEPTION_CODE } from './cargo.manager';
import { UserActivationState } from '../interfaces/userActivationState';

export interface VehicleAssignValidationResult {
  licensePlate: string;
  vehicle?: Vehicle;
  driver?: User;
  owner?: User;
  administrator?: User;
  isValid: boolean;
  message?: string;
  termsUnaccepted?: string;
  missingInfo?: { missingDocuments: string[]; unacceptedDocuments: string[]; invalidReferences: string[] };
  // true si el motivo de !isValid es especificamente el minimo de viajes del perfil de riesgo: el
  // llamador puede ofrecer el dialogo de "Continuar de todos modos" (permiso overrideRiskValidation)
  // en vez de un error sin salida.
  riskValidationBlocked?: boolean;
}

export interface VehicleDetail {
  vehicle: Vehicle;
  driver: User;
  owner: User;
  administrator?: User;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class VehicleAssignManager {

  private vehicles: { [key: string]: Vehicle } = {};
  private users: { [key: string]: User } = {};
  private userActivationStates: { [key: string]: UserActivationState[] } = {};
  private validationRules: ValidationRule[] = [];
  private mandatoryDocuments: MandatoryDocument[] = [];

  constructor(
    private utils: Utils,
    private authService: AuthService,
    private permissionRole: PermissionRole,
    private adminUsersService: AdminUsersService,
    private vehiclesService: VehiclesService,
    private patterns: Patterns,
    private cargoPaymentsService: CargoPaymentsService,
    private cargoService: CargoService
  ) { }

  public async validateVehiclesForAssign(
    licensePlates: string[],
    cargo?: Cargo,
    overrideRiskValidation?: boolean
  ): Promise<VehicleAssignValidationResult[]> {
    this.vehicles = {};
    this.users = {};
    this.userActivationStates = {};
    if (!licensePlates || !licensePlates.length)
      return [{ licensePlate: null, vehicle: null, isValid: false, message: 'No ha seleccionado un Vehículo' }];
    await this.authService.setConfigurationsSaasCompany();
    this.validationRules = await this.adminUsersService.getValidationRules().toPromise();
    this.mandatoryDocuments = await this.vehiclesService.getMandatoryDocuments();
    const results: VehicleAssignValidationResult[] = [];
    for (const licensePlate of licensePlates) {
      const vehicleDetail: VehicleDetail = await this.getVehicleDetail(licensePlate);
      if (vehicleDetail.error) {
        results.push({ licensePlate: licensePlate, isValid: false, message: vehicleDetail.error });
        continue;
      }
      results.push(await this.validateVehicle(licensePlate, vehicleDetail, cargo, overrideRiskValidation));
    }
    return results;
  }

  private async getVehicleDetail(licensePlate: string): Promise<VehicleDetail> {
    let vehicle: Vehicle;
    let driver: User;
    let owner: User;
    let administrator: User;
    //Consultar el vehículo
    if (!licensePlate || !licensePlate.trim()) return { vehicle: null, driver: null, owner: null, administrator: null, error: `No se ha seleccionado un vehículo` };
    licensePlate = licensePlate.toUpperCase();
    if (!this.patterns.GET_REGEX('LICENSE_PLATES').test(licensePlate)) return { vehicle: null, driver: null, owner: null, administrator: null, error: `La placa ${licensePlate} no es válida` };
    if (licensePlate in this.vehicles) vehicle = this.vehicles[licensePlate];
    else {
      vehicle = await this.getVehicle(licensePlate);
      this.vehicles[licensePlate] = vehicle;
    }
    if (!vehicle) return { vehicle: null, driver: null, owner: null, administrator: null, error: `No se encontró un vehículo con placa ${licensePlate}` };

    //Consultar el conductor
    const driverDocument = this.utils.getNestedValue(vehicle, 'driver.document');
    if (!driverDocument) return { vehicle: vehicle, driver: null, owner: null, administrator: null, error: `No se encontró el conductor del vehículo ${licensePlate} con documento ${driverDocument}` };
    if (driverDocument in this.users) driver = this.users[driverDocument];
    else {
      driver = await this.getDetailUserByDocument(driverDocument);
      this.users[driverDocument] = driver;
    }
    if (!driver) return { vehicle: vehicle, driver: null, owner: null, administrator: null, error: `No se encontró el conductor del vehículo ${licensePlate} con documento ${driverDocument}` };
    if (!(driverDocument in this.userActivationStates)) this.userActivationStates[driverDocument] = await this.adminUsersService.getUserActivationStates(driverDocument).toPromise();
    //Consultar el propietario
    const ownerDocument = this.utils.getNestedValue(vehicle, 'owner.document');
    if (!ownerDocument) return { vehicle: vehicle, driver: driver, owner: null, administrator: null, error: `No se encontró el propietario del vehículo ${licensePlate} con documento ${ownerDocument}` };
    if (ownerDocument in this.users) owner = this.users[ownerDocument];
    else {
      owner = await this.getDetailUserByDocument(ownerDocument);
      this.users[ownerDocument] = owner;
    }
    if (!owner) return { vehicle: vehicle, driver: driver, owner: null, administrator: null, error: `No se encontró el propietario del vehículo ${licensePlate} con documento ${ownerDocument}` };
    if (!(ownerDocument in this.userActivationStates)) this.userActivationStates[ownerDocument] = await this.adminUsersService.getUserActivationStates(ownerDocument).toPromise();
    //Consultar el administrador
    const administratorDocument = this.utils.getNestedValue(vehicle, 'administrator.document');
    if (!administratorDocument) return { vehicle: vehicle, driver: driver, owner: owner, administrator: null };
    if (administratorDocument in this.users) administrator = this.users[administratorDocument];
    else {
      administrator = await this.getDetailUserByDocument(administratorDocument);
      this.users[administratorDocument] = administrator;
    }
    if (!administrator) return { vehicle: vehicle, driver: driver, owner: owner, administrator: null, error: `No se encontró el administrador del vehículo ${licensePlate} con documento ${administratorDocument}` };
    if (!(administratorDocument in this.userActivationStates)) this.userActivationStates[administratorDocument] = await this.adminUsersService.getUserActivationStates(administratorDocument).toPromise();
    return { vehicle: vehicle, driver: driver, owner: owner, administrator: administrator };
  }

  private async getVehicle(licensePlate: string): Promise<Vehicle> {
    try {
      const vehicle = await this.vehiclesService.getVehicleFromOwnCompany(licensePlate).toPromise();
      if (!vehicle) return null;
      return vehicle;
    } catch (error) {
      return null;
    }
  }

  private async getDetailUserByDocument(userDocument: string): Promise<User> {
    if (!userDocument) return null;
    try {
      const user = await this.adminUsersService.getUsersDetailByDocument(userDocument).toPromise();
      return user ? user : null;
    } catch (error) {
      return null;
    }
  }

  private async validateVehicle(
    licensePlate: string,
    vehicleDetails: VehicleDetail,
    cargo?: Cargo,
    overrideRiskValidation?: boolean
  ): Promise<VehicleAssignValidationResult> {
    const { vehicle, driver, owner, administrator } = vehicleDetails;
    //Validar asignación de vehículo especial
    const canUseSpecialLicense = this.permissionRole.hasPermission(Permission.cargo.module, Permission.cargo.specialLicensePlate);
    if (vehicle.id === 'C0N111' && !canUseSpecialLicense)
      return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `No tiene permisos para asignar el vehículo ${licensePlate}` };
    //Validar campos obligatorios del vehículo
    const vehicleRequiredFieldsResults = this.validateVehicleRequiredFields(vehicle);
    if (!vehicleRequiredFieldsResults.isValid) return { licensePlate: licensePlate, vehicle: vehicle, isValid: false, message: vehicleRequiredFieldsResults.error };

    const stateCompany = this.utils.getNestedValue(vehicle, 'stateCompany');
    if (stateCompany && !stateCompany.active) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `El vehículo ${licensePlate} se encuentra inactivo por las reglas de la empresa` };
    const stateDescription = this.utils.getNestedValue(vehicle, 'state.description');
    if (stateDescription === 'Estudio de seguridad en proceso') return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `El vehículo ${licensePlate} se encuentra en estudio de seguridad` };
    if (!vehicle.state || !vehicle.state.active) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `El vehículo ${licensePlate} se encuentra inactivo` };

    //Validaciones de la información obligatoria del vehículo
    const missingInfo = await this.vehiclesService.checkMissingInfo(vehicle, driver, this.mandatoryDocuments);
    if (missingInfo.missingDocuments.length || missingInfo.unacceptedDocuments.length || missingInfo.invalidReferences.length) {
      return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `Error en la Información obligatoria del Vehículo ${licensePlate}`, missingInfo: missingInfo };
    }
    //Validar RTM y SOAT del vehículo
    const { valid: validRTM, errors: errorsRTM } = this.vehiclesService.validateExpirationRTM(vehicle);
    if (!validRTM) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: errorsRTM[0] as string };
    const { valid: validSOAT, errors: errorsSOAT } = this.vehiclesService.validateExpirationSOAT(vehicle);
    if (!validSOAT) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: errorsSOAT[0] as string };
    //Validar reglas de validación del usuario
    const isRootNit = this.authService && this.authService.getCompany() && this.authService.getCompany().companyId === environment.rootNit;

    //Validar Estado del administrador
    if (administrator) {
      const administratorDocument = this.utils.getNestedValue(administrator, 'information.document');
      const adminStateByValidationRules = await this.adminUsersService.getUserStateByValidationRules(administrator, 'Admin', this.validationRules, this.userActivationStates[administratorDocument]);
      const adminStateDescription = this.utils.getNestedValue(adminStateByValidationRules, 'description');
      if ((!adminStateByValidationRules || !adminStateByValidationRules.active) && !isRootNit)
        return {
          ...vehicleDetails,
          licensePlate: licensePlate,
          isValid: false,
          message: `El administrador del vehículo ${licensePlate} no cumple con las reglas de validación ${adminStateDescription ? ': ' + adminStateDescription : ''}`
        };
    } else {
      //Validar Estado del propietario
      const ownerDocument = this.utils.getNestedValue(owner, 'information.document');
      const ownerStateByValidationRules = await this.adminUsersService.getUserStateByValidationRules(owner, 'Owner', this.validationRules, this.userActivationStates[ownerDocument]);
      const ownerStateDescription = this.utils.getNestedValue(ownerStateByValidationRules, 'description');
      if ((!ownerStateByValidationRules || !ownerStateByValidationRules.active) && !isRootNit)
        return {
          ...vehicleDetails,
          licensePlate: licensePlate,
          isValid: false,
          message: `El propietario del vehículo ${licensePlate} no cumple con las reglas de validación ${ownerStateDescription ? ': ' + ownerStateDescription : ''}`
        };
    }
    //Validar Estado del conductor
    const driverDocument = this.utils.getNestedValue(driver, 'information.document');
    const driverStateByValidationRules = await this.adminUsersService.getUserStateByValidationRules(driver, 'Driver', this.validationRules, this.userActivationStates[driverDocument]);
    const driverStateDescription = this.utils.getNestedValue(driverStateByValidationRules, 'description');
    if ((!driverStateByValidationRules || !driverStateByValidationRules.active))
      return {
        ...vehicleDetails,
        licensePlate: licensePlate,
        isValid: false,
        message: `El conductor del vehículo ${licensePlate} no cumple con las reglas de validación ${driverStateDescription ? ': ' + driverStateDescription : ''}`
      };
    if (!driver.driverLicenseCategory || !driver.driverLicenseCategory.length)
      return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `El conductor del vehículo ${licensePlate} no tiene licencia(s) de conducción configurada(s)` };
    const licenseCategoriesResults = this.validateLicenseCategories(licensePlate, driver.driverLicenseCategory, vehicle.vehicleType.name);
    if (!licenseCategoriesResults.valid) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: licenseCategoriesResults.message };
    //Validar Términos y condiciones del conductor
    const termsAndConditionsResults = this.validateTermsAndConditionsUser(driver);
    if (!termsAndConditionsResults.isValid) return { ...vehicleDetails, licensePlate: licensePlate, driver: driver, isValid: false, termsUnaccepted: termsAndConditionsResults.error };
    //Validar Documentos Legales del conductor
    const isLegalDocumentsValid = this.validateLegalDocumentsUser(driver);
    if (!isLegalDocumentsValid)
      return { ...vehicleDetails, licensePlate: licensePlate, driver: driver, isValid: false, termsUnaccepted: `el Contrato de Factoring Comercial` };
    //Validar Contrato de Vinculación de Manifiesto del conductor
    const manifestBindingContractResults = await this.validateManifestBindingContractUser(driver);
    if (!manifestBindingContractResults.isValid)
      return { ...vehicleDetails, licensePlate: licensePlate, driver: driver, isValid: false, termsUnaccepted: manifestBindingContractResults.error };
    //Validar Política de tratamiento de datos personales - Logistics del conductor
    const personalDataPolicyLogisticsResults = await this.validatePersonalDataPolicyLogisticsUser(driver);
    if (!personalDataPolicyLogisticsResults.isValid)
      return { ...vehicleDetails, licensePlate: licensePlate, driver: driver, isValid: false, termsUnaccepted: personalDataPolicyLogisticsResults.error };

    //Validaciones del servicio
    if (cargo) {
      const cargoVehicleType = this.utils.getNestedValue(cargo, 'cargoFeature.vehicleType.name');
      if (!cargoVehicleType) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: "El tipo de vehículo no se encuentra configurado en el servicio" };
      if (cargoVehicleType && cargoVehicleType.trim().toLowerCase() !== vehicle.vehicleType.name.trim().toLowerCase())
        return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: `El tipo del vehículo ${licensePlate} no coincide con el tipo de vehículo del servicio ${cargoVehicleType}` };
      // Pre-chequeo del mínimo de viajes que exige el perfil de riesgo del servicio, para no hacerle
      // perder tiempo al usuario completando la inspección si la asignación real se va a rechazar. La
      // regla vive solo en el backend (CargoBusinessImpl.validateVehicleTripRequirement); aquí solo se
      // consulta antes.
      if (cargo.id) {
        try {
          await this.cargoService.validateVehicleTripRequirement(cargo.id, licensePlate, overrideRiskValidation).toPromise();
        } catch (error) {
          const message = this.utils.getNestedValue(error, 'error.message') ||
            'El vehículo no cumple con el número mínimo de viajes requerido para el valor declarado del servicio';
          const riskValidationBlocked = this.utils.getNestedValue(error, 'error.code') === VEHICLE_TRIP_VALIDATION_EXCEPTION_CODE;
          return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message, riskValidationBlocked };
        }
      }
      if (cargo.ministry) {
        const ownFleetResults = this.validateOwnFleet(vehicle, cargo);
        if (!ownFleetResults.isValid) return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: ownFleetResults.error };
        const vehicleGrossWeight = this.utils.getNestedValue(vehicle, 'grossWeight');
        const exceptionsGrossWeightValidation = ['AUTOMOVIL', 'MOTOCICLETA', 'MOTOCARRO', 'TRACTOCAMION', 'VOLQUETA'];
        if (!exceptionsGrossWeightValidation.includes(cargoVehicleType) && !vehicleGrossWeight &&
          this.utils.isTransportService(cargo) && !this.utils.isSameCity(cargo))
          return { ...vehicleDetails, licensePlate: licensePlate, isValid: false, message: VehicleMessages.NO_VEHICLE_GROSS_WEIGHT as string };
      }
    }

    return { ...vehicleDetails, licensePlate: licensePlate, isValid: true };
  }

  private validateTermsAndConditionsUser(driver: User): { isValid: boolean, error?: string } {
    if (!driver || !driver.lastUserTerm || !driver.lastUserTerm.state) return { isValid: false, error: `Términos y condiciones del servicio` };
    if (!driver.lastPrivacyPolicies || !driver.lastPrivacyPolicies.state) return { isValid: false, error: `Política de privacidad` };
    if (!driver.lastPersonalDataPolicy || !driver.lastPersonalDataPolicy.state) return { isValid: false, error: `Política de tratamiento de datos personales` };
    return { isValid: true };
  }

  private validateLegalDocumentsUser(driver: User): boolean {
    if (!this.isTeclogiUserOrClient) return true;
    return !!(driver && driver.factoringContractLoggiFintech && driver.factoringContractLoggiFintech.state);
  }

  private async validateManifestBindingContractUser(driver: User): Promise<{ isValid: boolean, error?: string }> {
    if (!this.isTeclogiUserOrClient) return { isValid: true };
    const hasManifestBindingContract = !!(driver && driver.manifestBindingContract && driver.manifestBindingContract.state);
    if (!hasManifestBindingContract) return { isValid: false, error: `el Contrato de Vinculación de Manifiesto` };
    const hasManifestBindingContractLogistics = !!(driver && driver.manifestBindingContractLogistics && driver.manifestBindingContractLogistics.state);
    if (!hasManifestBindingContractLogistics) return { isValid: false, error: `el Contrato de Vinculación de Manifiesto - Logistics` };
    return { isValid: true };
  }

  private async validatePersonalDataPolicyLogisticsUser(driver: User): Promise<{ isValid: boolean, error?: string }> {
    if (!this.isTeclogiUserOrClient) return { isValid: true };
    const hasPersonalDataPolicyLogistics = !!(driver && driver.personalDataPolicyLogistics && driver.personalDataPolicyLogistics.state);
    if (!hasPersonalDataPolicyLogistics) return { isValid: false, error: `Política de tratamiento de datos personales - Logistics` };
    return { isValid: true };
  }

  private validateOwnFleet(vehicle: Vehicle, cargo?: Cargo): { isValid: boolean, error?: string } {
    const skipFreightRNDCReport = this.utils.getNestedValue(cargo, 'shippingCost.skipFreightRNDCReport');
    if (!this.cargoPaymentsService.isShippingCostZero(cargo) && !skipFreightRNDCReport) return { isValid: true };
    if (!this.authService || !this.authService.getCompanySaaS() || !this.authService.getCompanySaaS().companyId)
      return { isValid: false, error: ServiceMessages.GENERAL_RETRY_INDICATION };
    if (this.utils.vehicleIsOwnFleet(vehicle, this.authService.getCompanySaaSConfig())) return { isValid: true };
    if (skipFreightRNDCReport) return { isValid: false, error: `No puedes asignar el vehiculo ${vehicle.id} ya que, para servicios cuyo flete corresponde solo para uso interno de LoggiApp, el tenedor del vehiculo debe ser el mismo generador del manifiesto (empresa de transporte).` };
    return { isValid: false, error: `No puedes asignar el vehiculo ${vehicle.id} ya que para servicios con tarifa y/o flete 0 el tenedor del vehiculo debe ser el mismo generador del manifiesto (empresa de transporte).` };
  }
  private validateVehicleRequiredFields(vehicle: Vehicle): { isValid: boolean, error?: string } {
    const vehicleType = this.utils.getNestedValue(vehicle, 'vehicleType.name');
    if (!vehicleType || vehicleType.trim() === '')
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un tipo configurado` };
    const vehicleBodyworkType = this.utils.getNestedValue(vehicle, 'bodywork.description');
    if (!vehicleBodyworkType || vehicleBodyworkType.trim() === '')
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un tipo de carrocería configurado` };
    if (!vehicle.registrationDate || vehicle.registrationDate.trim() === '')
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene una fecha de matriculación registrada` };
    if (vehicleType === 'TRACTOCAMION' && (vehicle.axles === null || vehicle.axles === undefined || vehicle.axles <= 0))
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un número de ejes registrado` };
    if (vehicleType === 'TRACTOCAMION' && (vehicle.emptyWeight === null || vehicle.emptyWeight === undefined || vehicle.emptyWeight <= 0))
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un peso vacío registrado` };
    if (!vehicle.serviceType || vehicle.serviceType.trim() === '')
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un tipo de servicio registrado` };
    if (!vehicle.manufacturingYear || vehicle.manufacturingYear.trim() === '')
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un año modelo registrado` };
    if (vehicleType === "TRACTOCAMION" && !vehicle.trailerId)
      return { isValid: false, error: `El vehículo ${vehicle.id} no tiene un trailer registrado` };
    return { isValid: true };
  }

  validateLicenseCategories(licensePlate: string, driverLicenses: DriverLicenseCategory[], vehicleType: string): { valid: boolean, message: string } {
    const requiredLicenses = licenseCategories[vehicleType];
    if (!requiredLicenses) return { valid: false, message: `El vehículo ${licensePlate} no tiene un tipo de vehículo valido` };
    const validLicenses = driverLicenses.filter(license =>
      license && license.category && requiredLicenses.includes(license.category)
    );
    if (validLicenses.length === 0) return { valid: false, message: `El conductor del vehículo ${licensePlate} no cuenta con la licencia requerida para este tipo de vehículo. Licencia(s) requerida(s): ${requiredLicenses.join(', ')}` };
    const hasActiveLicense = validLicenses.some(license => {
      const expirationDate = DateManager.stringToDate(license.expirationDate, 'YYYY-MM-DDTHH:mm:ssT');
      const isExpired = DateManager.isSameOrBefore(expirationDate, new Date());
      return license.active && !isExpired;
    });
    if (!hasActiveLicense) return { valid: false, message: `El conductor del vehículo ${licensePlate} cuenta con la licencia requerida, pero está vencida o inactiva.` };
    return { valid: true, message: "" };
  }

  private get isTeclogiUserOrClient(): boolean {
    return this.authService && this.authService.getCompanySaaS() &&
      Companies.companiesNIT.TECLOGI === this.authService.getCompanySaaS().companyId;
  }

}
